import { cn } from '@/lib/utils'
import { Globe, Mail, Share2, Users } from 'lucide-react'

type Platform = 'facebook' | 'google' | 'apple' | 'email' | 'referral' | 'social' | 'organic'

interface PlatformLogoProps {
  platform: Platform | string
  size?: 'sm' | 'md' | 'lg'
  className?: string
  showLabel?: boolean
}

const sizeClasses = {
  sm: 'w-4 h-4',
  md: 'w-5 h-5',
  lg: 'w-6 h-6',
}

/**
 * Meta (Facebook) logo - simplified "f" icon
 */
function MetaLogo({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path d="M12 2.04c-5.5 0-10 4.49-10 10.02 0 5 3.66 9.15 8.44 9.9v-7H7.9v-2.9h2.54V9.85c0-2.51 1.49-3.89 3.78-3.89 1.09 0 2.23.19 2.23.19v2.47h-1.26c-1.24 0-1.63.77-1.63 1.56v1.88h2.78l-.45 2.9h-2.33v7a10 10 0 0 0 8.44-9.9c0-5.53-4.5-10.02-10-10.02Z" />
    </svg>
  )
}

/**
 * Google "G" logo - simplified colorful G
 */
function GoogleLogo({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className}>
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  )
}

/**
 * Apple logo - simplified apple icon
 */
function AppleLogo({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11Z" />
    </svg>
  )
}

/**
 * Normalizes platform display names to platform identifiers
 * Handles: "Facebook Ads", "Google Ads", "Apple Ads", "Meta", etc.
 */
function normalizePlatform(platform: string): Platform | null {
  const lower = platform.toLowerCase()

  // Direct matches
  if (lower === 'facebook' || lower === 'google' || lower === 'apple') {
    return lower as Platform
  }

  // Display name matches
  if (lower.includes('facebook') || lower.includes('meta')) {
    return 'facebook'
  }
  if (lower.includes('google')) {
    return 'google'
  }
  if (lower.includes('apple')) {
    return 'apple'
  }
  if (lower === 'email' || lower.includes('email') || lower.includes('outreach')) {
    return 'email'
  }
  if (lower === 'referral' || lower.includes('referral') || lower.includes('affiliate')) {
    return 'referral'
  }
  if (lower === 'social') {
    return 'social'
  }
  if (lower === 'organic') {
    return 'organic'
  }

  return null
}

/**
 * Renders a platform logo (Meta/Google/Apple) with optional label
 */
export function PlatformLogo({
  platform,
  size = 'md',
  className,
  showLabel = false,
}: PlatformLogoProps) {
  const normalizedPlatform = normalizePlatform(platform)

  const logoClassName = cn(sizeClasses[size], 'flex-shrink-0')

  const renderLogo = () => {
    switch (normalizedPlatform) {
      case 'facebook':
        return <MetaLogo className={cn(logoClassName, 'text-[#1877F2]')} />
      case 'google':
        return <GoogleLogo className={logoClassName} />
      case 'apple':
        return <AppleLogo className={cn(logoClassName, 'text-foreground')} />
      case 'email':
        return <Mail className={cn(logoClassName, 'text-violet-600 dark:text-violet-400')} />
      case 'referral':
        return <Users className={cn(logoClassName, 'text-amber-600 dark:text-amber-400')} />
      case 'social':
        return <Share2 className={cn(logoClassName, 'text-sky-600 dark:text-sky-400')} />
      case 'organic':
        return <Globe className={cn(logoClassName, 'text-emerald-600 dark:text-emerald-400')} />
      default:
        // Return null for unrecognized platforms - will show fallback text
        return null
    }
  }

  const getLabel = () => {
    switch (normalizedPlatform) {
      case 'facebook':
        return 'Meta'
      case 'google':
        return 'Google'
      case 'apple':
        return 'Apple'
      case 'email':
        return 'Email'
      case 'referral':
        return 'Referral'
      case 'social':
        return 'Social'
      case 'organic':
        return 'Organic'
      default:
        return platform
    }
  }

  // Fallback for unrecognized platforms
  const logo = renderLogo()
  if (!logo) {
    return <span className={cn('text-sm text-muted-foreground', className)}>{platform}</span>
  }

  if (showLabel) {
    return (
      <div className={cn('flex items-center gap-1.5', className)}>
        {logo}
        <span className="text-xs text-muted-foreground">{getLabel()}</span>
      </div>
    )
  }

  return <div className={cn('flex items-center justify-center', className)}>{logo}</div>
}
